import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback
} from "react";
import { BrowserNotificationService } from "../../services/BrowserNotificationService";

interface NotificationContextValue {
  permission: NotificationPermission;
  isSupported: boolean;
  requestPermission: () => Promise<NotificationPermission>;
  showBrowserNotification: (options: {
    title: string;
    body: string;
    data?: any;
    tag?: string;
    icon?: string;
    silent?: boolean;
  }) => Promise<void>;
  /** Notificación del sistema con icono Intiza por defecto; tag opcional para deduplicar */
  showNotification: (
    title: string,
    body: string,
    data?: any,
    tag?: string
  ) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue>({
  permission: "default",
  isSupported: false,
  requestPermission: async () => "denied",
  showBrowserNotification: async () => {},
  showNotification: async () => {}
});

export const useNotification = () => useContext(NotificationContext);

interface NotificationProviderProps {
  children: React.ReactNode;
}

/**
 * Provider para gestionar notificaciones del navegador.
 * Los eventos de socket (p. ej. WhatsApp CallReceived) los enlaza BrowserNotificationSocketBridge.
 */
export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children
}) => {
  const [permission, setPermission] =
    useState<NotificationPermission>("default");
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    setIsSupported(BrowserNotificationService.isSupported());
    setPermission(BrowserNotificationService.getPermission());
  }, []);

  const requestPermission = useCallback(async () => {
    const newPermission = await BrowserNotificationService.requestPermission();
    setPermission(newPermission);
    return newPermission;
  }, []);

  const showBrowserNotification = useCallback(
    async ({
      title,
      body,
      data,
      tag,
      icon,
      silent
    }: {
      title: string;
      body: string;
      data?: any;
      tag?: string;
      icon?: string;
      silent?: boolean;
    }) => {
      if (!BrowserNotificationService.hasPermission()) {
        console.warn("No se tienen permisos para mostrar notificaciones");
        return;
      }

      await BrowserNotificationService.showNotification(title, {
        body,
        data,
        tag: tag || title,
        icon,
        requireInteraction: false,
        silent: typeof silent === "boolean" ? silent : false
      });
    },
    []
  );

  const showNotification = useCallback(
    async (title: string, body: string, data?: any, tag?: string) => {
      await showBrowserNotification({ title, body, data, tag });
    },
    [showBrowserNotification]
  );

  return (
    <NotificationContext.Provider
      value={{
        permission,
        isSupported,
        requestPermission,
        showBrowserNotification,
        showNotification
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
